<template>
  <div class="old-book-page">
    <Header />
    
    <main class="main-content">
      <div class="container marketplace-container">
        
        <!-- Compact Unified Toolbar Header -->
        <div class="ob-compact-toolbar">
          <!-- Top Row: Brand / Title + Primary Action -->
          <div class="toolbar-top-row">
            <div class="toolbar-brand">
              <h1 class="toolbar-title">Chợ Sách Cũ</h1>
              <span class="toolbar-dot">•</span>
              <span class="toolbar-subtitle">Sàn trao đổi & gian hàng nhượng lại sách</span>
            </div>

            <div class="toolbar-top-actions">
              <span class="toolbar-count-badge">
                <strong>{{ totalElements }}</strong> gian hàng
              </span>

              <button @click="goToCreateBook" class="btn-shopee-post">
                <icon icon="fa-solid fa-plus" class="btn-post-icon" />
                <span>Đăng bán gian hàng mới</span>
              </button>
            </div>
          </div>

          <!-- Bottom Row: Integrated Compact Search & Filters Toolbar -->
          <div class="toolbar-controls-row">
            <!-- Search Input Box -->
            <div class="toolbar-search-box">
              <icon icon="fa-solid fa-magnifying-glass" class="search-icon" />
              <input 
                type="text" 
                v-model="searchKeyword" 
                placeholder="Tìm tên gian hàng, tên sách, tác giả, thể loại..."
                @keyup.enter="handleSearch"
                @input="onSearchInput"
                class="toolbar-search-input"
              />
              <button v-if="searchKeyword" @click="clearSearch" type="button" class="btn-clear-icon" title="Xóa từ khóa">
                <icon icon="fa-solid fa-xmark" />
              </button>
            </div>

            <!-- Sale Type Filter -->
            <div class="toolbar-type-select">
              <select v-model="selectedSaleType" @change="handleSearch" class="tb-select-input" title="Hình thức bán">
                <option :value="null">Mọi hình thức (Combo / Lẻ)</option>
                <option :value="0">Cho phép mua lẻ</option>
                <option :value="1">Bắt buộc mua combo</option>
              </select>
            </div>

            <!-- Price Quick Select + Range Inputs -->
            <div class="toolbar-price-inputs">
              <span class="tb-price-label"><icon icon="fa-solid fa-tags" class="me-1" />Giá</span>
              <select v-model="quickPrice" @change="onQuickPriceChange" class="tb-price-quick" title="Chọn nhanh mức giá">
                <option v-for="q in quickPriceOptions" :key="q.id" :value="q.id">{{ q.label }}</option>
              </select>
              <div class="tb-price-input-group">
                <input
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9.]*"
                  :value="formatPriceInput(priceFromK)"
                  placeholder="Từ"
                  title="Giá từ (VNĐ)"
                  class="tb-price-input"
                  @input="onPriceFromInput"
                />
                <span class="tb-price-sep">–</span>
                <input
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9.]*"
                  :value="formatPriceInput(priceToK)"
                  placeholder="Đến"
                  title="Giá đến (VNĐ)"
                  class="tb-price-input"
                  @input="onPriceToInput"
                />
                <span class="tb-price-unit">đ</span>
              </div>
            </div>

            <!-- Status Selector -->
            <div class="toolbar-status-select">
              <select v-model="selectedStatus" @change="handleSearch" class="tb-select-input">
                <option :value="null">Mọi trạng thái</option>
                <option :value="0">Đang bán</option>
                <option :value="1">Đã nhận cọc</option>
                <option :value="2">Đã bán</option>
                <option :value="3">Tạm ngưng</option>
              </select>
            </div>

            <!-- Price Sort Selector -->
            <div class="toolbar-sort-select">
              <select v-model="selectedSort" @change="handleSearch" class="tb-select-input">
                <option value="default">Sắp xếp giá: Mặc định</option>
                <option value="price_asc">Giá: Thấp đến Cao ↑</option>
                <option value="price_desc">Giá: Cao đến Thấp ↓</option>
              </select>
            </div>
          </div>

          <!-- Active Filters Bar (Shown only when filtering) -->
          <div v-if="hasActiveFilters" class="toolbar-active-bar">
            <span class="active-lbl"><icon icon="fa-solid fa-filter" class="me-1 text-emerald-600" /> Đang lọc:</span>
            
            <span v-if="searchKeyword" class="tb-filter-tag" @click="clearSearch">
              "{{ searchKeyword }}" <icon icon="fa-solid fa-xmark" class="ms-1" />
            </span>

            <span v-if="selectedSaleType !== null" class="tb-filter-tag" @click="selectedSaleType = null; handleSearch()">
              {{ selectedSaleType === 1 ? 'Bắt buộc mua combo' : 'Cho phép mua lẻ' }} <icon icon="fa-solid fa-xmark" class="ms-1" />
            </span>

            <span v-if="priceFromK !== '' || priceToK !== ''" class="tb-filter-tag" @click="clearPriceFilter">
              Giá: {{ formatPriceFilter() }} <icon icon="fa-solid fa-xmark" class="ms-1" />
            </span>

            <span v-if="selectedStatus !== null" class="tb-filter-tag" @click="selectedStatus = null; handleSearch()">
              {{ getListingStatusLabel(selectedStatus) }} <icon icon="fa-solid fa-xmark" class="ms-1" />
            </span>

            <span v-if="selectedSort !== 'default'" class="tb-filter-tag" @click="selectedSort = 'default'; handleSearch()">
              {{ selectedSort === 'price_asc' ? 'Giá tăng dần' : 'Giá giảm dần' }} <icon icon="fa-solid fa-xmark" class="ms-1" />
            </span>

            <button @click="resetFilters" class="btn-tb-reset">
              <icon icon="fa-solid fa-rotate-left" class="me-1" /> Xóa lọc
            </button>
          </div>
        </div>

        <!-- Book Filter Banner (entered from Book Detail page) -->
        <div v-if="bookIdFilter" class="ob-book-filter-banner">
          <div class="ob-book-filter-info">
            <icon icon="fa-solid fa-book-open-reader" class="ob-book-filter-icon" />
            <div class="ob-book-filter-text">
              <span class="ob-book-filter-label">Đang xem gian hàng có bán cuốn sách</span>
              <span class="ob-book-filter-title">{{ filteredBookName || 'cuốn sách này' }}</span>
            </div>
          </div>
          <button @click="clearBookFilter" type="button" class="btn-ob-clear-book-filter">
            <icon icon="fa-solid fa-xmark" class="me-1" /> Bỏ lọc
          </button>
        </div>

        <!-- Loading Skeletons -->
        <div v-if="isLoading" class="shopee-loading-section">
          <div class="shopee-loading-indicator">
            <icon icon="fa-solid fa-circle-notch" class="shopee-loading-spinner" />
            <span>Đang tải danh sách gian hàng sách cũ...</span>
          </div>
          <div class="shopee-grid">
            <div v-for="n in 10" :key="n" class="skeleton-shopee-card">
              <div class="skeleton-img"></div>
              <div class="skeleton-text line1"></div>
              <div class="skeleton-text line2"></div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="booths.length === 0" class="shopee-empty-state">
          <icon icon="fa-solid fa-box-open" class="empty-icon" />
          <h3>Không tìm thấy gian hàng sách cũ phù hợp</h3>
          <p>Thử từ khóa khác hoặc xóa bộ lọc để khám phá thêm nhiều gian hàng sách cũ phong phú.</p>
          <button @click="resetFilters" class="btn-shopee-reset">Xóa bộ lọc</button>
        </div>

        <!-- HIGH DENSITY BOOTH GRID -->
        <div v-else class="shopee-grid">
          <div 
            v-for="booth in booths" 
            :key="booth.id"
            class="shopee-card"
            @click="goToDetail(booth.id)"
          >
            <!-- Cover Frame -->
            <div class="shopee-card-img" @click.stop="openQuickDrawer(booth)">
              <img 
                :src="getCoverUrl(booth.coverImage || (booth.books && booth.books[0] && booth.books[0].cover))" 
                :alt="booth.name" 
                loading="lazy" 
                @error="handleImageError" 
              />
              
              <!-- Badges Container on Top-Left -->
              <div class="shopee-badge-stack">
                <span class="shopee-cond-tag" :class="{ 'is-combo': booth.totalBooks > 1 }">
                  <icon :icon="booth.totalBooks > 1 ? 'fa-solid fa-layer-group' : 'fa-solid fa-book'" class="me-1" />
                  {{ booth.totalBooks > 1 ? ('Combo ' + booth.totalBooks + ' cuốn') : '1 cuốn' }}
                </span>
                <span class="shopee-sale-type-tag" :class="booth.saleType === 1 ? 'type-combo' : 'type-single'">
                  {{ booth.saleType === 1 ? 'Mua combo' : 'Mua lẻ/cả bộ' }}
                </span>
              </div>

              <!-- Sale Off Tag if any -->
              <div v-if="booth.discountRate && booth.discountRate > 0" class="my-booth-sale-tag">
                -{{ booth.discountRate }}%
              </div>

              <!-- Quick View Button -->
              <button class="shopee-btn-quick" @click.stop="openQuickDrawer(booth)" title="Xem nhanh thông tin">
                <icon icon="fa-solid fa-eye" /> Xem nhanh
              </button>
            </div>

            <!-- Card Body -->
            <div class="shopee-card-body">
              <h3 class="shopee-card-title font-serif" :title="booth.name" @click="goToDetail(booth.id)">
                {{ booth.name }}
              </h3>

              <!-- Authors & Categories Meta -->
              <div class="shopee-card-meta-row" v-if="getAuthorsList(booth).length > 0">
                <span class="meta-item-author">
                  <icon icon="fa-solid fa-feather-pointed" class="me-1" />{{ getAuthorsList(booth).slice(0, 2).join(', ') }}
                </span>
              </div>

              <!-- Books Inside Summary (only when >= 2 books) -->
              <p class="shopee-card-desc" v-if="booth.books && booth.books.length > 1">
                <strong class="text-emerald-700">Gồm:</strong> {{ booth.books.map(b => b.name).join(' • ') }}
              </p>
              
              <!-- Price Row -->
              <div class="shopee-card-price-row mt-auto">
                <div class="d-flex align-items-baseline gap-2 flex-wrap">
                  <span class="shopee-price">{{ formatPrice(booth.price) }}</span>
                  <span v-if="booth.originalPrice && booth.originalPrice > booth.price" class="shopee-original-price">
                    {{ formatPrice(booth.originalPrice) }}
                  </span>
                </div>
              </div>

              <!-- Card Footer: Seller Name & Status -->
              <div class="shopee-public-footer mt-2 pt-2 border-top">
                <span class="public-seller-name" :title="'Người bán: ' + (booth.createByName || 'Người dùng')">
                  <icon icon="fa-solid fa-store" class="me-1 text-emerald-600" />
                  {{ booth.createByName || 'Người dùng' }}
                </span>
                <span class="public-status-tag" :class="'status-tag-' + booth.status">
                  <span class="status-dot"></span>
                  {{ getListingStatusLabel(booth.status) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="shopee-pagination">
          <ul class="pagination-list">
            <li :class="{ disabled: currentPage === 0 }">
              <button class="page-btn" @click="fetchBooths(currentPage - 1)"><icon icon="fa-solid fa-chevron-left" /></button>
            </li>
            <li v-for="p in visiblePages" :key="p" :class="{ active: p === currentPage }">
              <span v-if="p === '...'" class="page-dots">…</span>
              <button v-else class="page-btn" @click="fetchBooths(p)">{{ p + 1 }}</button>
            </li>
            <li :class="{ disabled: currentPage >= totalPages - 1 }">
              <button class="page-btn" @click="fetchBooths(currentPage + 1)"><icon icon="fa-solid fa-chevron-right" /></button>
            </li>
          </ul>
        </div>

      </div>
    </main>

    <!-- Shared Quick View Drawer & Zoom Component (Public Mode) -->
    <OldBookQuickDrawer 
      :booth="drawerBooth" 
      mode="public" 
      @close="drawerBooth = null" 
    />

    <Footer />
  </div>
</template>

<script src="./old-book-list.js"></script>
<style scoped src="./old-book-list.css"></style>
